// The Claude card (Dominik 22.10.2026: "Hatten wir nicht gesagt, dass wir AI Sachen anders einbinden?"; boards
// "Claude: nach dem Bezug", "… oben auf der Seite eines Bezugs", "… denkt nach, Fehler, ohne Schlüssel"): a quiet label
// with the sparkle, the heading is the action, one line of reasons, the change as old → new in its own row, the
// source with an (i), "Übernehmen" and "Weiter fragen"; adopted, "Übernommen" with "Rückgängig". While Claude thinks it
// says what Claude reads, after 20 s that the suggestion comes later. Decisions in src/ai/view.ts (claudeCardView);
// Claude's words are model output and stay React text nodes (never HTML).
import { useEffect, useId, useState } from 'react';
import type { ShotItem } from '@brewcore/app-contract';
import { earlierShots, claudeCardView, type ClaudeCardView } from '../../ai/view.js';
import { Button, Icon, IconButton, InfoTip, NavList, Sheet } from '../components/index.js';
import { useMachine, useStore } from '../machine-context.js';
import { useApply } from './ApplyBox.js';
import { ChatSheet } from './ChatSheet.js';
import { useAssistant, useOpenClaudeSettings } from './use-assistant.js';

export type ClaudeCard = NonNullable<ReturnType<typeof useClaudeCard>>;

/**
 * Everything the card needs for one shot: its view, asking, hiding, adopting (one set command, AD-11) and undoing.
 * Loads the account's conversation about the shot once there is a key.
 */
export function useClaudeCard(shot: ShotItem | null, place: 'done' | 'shot') {
  const { ctx, client, machineId, send } = useMachine();
  const { assistant, view } = useAssistant();
  const shots = useStore(client.shots(machineId));
  const beans = useStore(client.beans(machineId));
  const adopt = useApply();
  const [undoing, setUndoing] = useState(false);
  const hasKey = !!view.account?.key;
  const id = shot?.id ?? null;
  useEffect(() => {
    if (hasKey && id) void assistant.loadThread(machineId, id);
  }, [assistant, machineId, id, hasKey]);
  if (!shot) return null;
  const ai = assistant.shot(machineId, shot.id);
  const card: ClaudeCardView = claudeCardView({
    ctx,
    shot,
    assistant: view,
    ai,
    currentBean: beans.items.find((b) => b.current) ?? null,
    earlier: earlierShots(shot, shots.items),
    place,
    sending: adopt.sending,
  });
  return {
    shot,
    card,
    sending: adopt.sending !== null,
    undoing,
    ask: () => void assistant.suggest(machineId, shot.id, ctx.lang),
    hide: () => assistant.hide(machineId, shot.id),
    apply: () => {
      if (!card.apply) return;
      const index = card.index;
      void adopt.apply(card.apply, (v) => void assistant.markApplied(machineId, shot.id, index, v.changes));
    },
    undo: async () => {
      if (!card.undo || undoing) return;
      setUndoing(true);
      const outcome = await send(card.undo);
      if (outcome.result === 'done') await assistant.markUndone(machineId, shot.id, card.index);
      setUndoing(false);
    },
  };
}

/** The card's inner box (after the shot inside the taste card, on the shot's page in a card of its own). */
export function ClaudeBox({ c, testId = 'claude-shot' }: { c: ClaudeCard; testId?: string }) {
  const { ctx } = useMachine();
  const openSettings = useOpenClaudeSettings();
  const [chat, setChat] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuTitle = useId();
  const { card } = c;
  if (card.state === 'none') return null;
  const askMore = (
    <button type="button" className="link-btn" onClick={() => setChat(true)} data-testid="claude-chat-open">
      {ctx.tr('aiAskMore')}
    </button>
  );
  const hasMenu = card.state === 'done' || card.state === 'applied';
  return (
    <section
      className={`ai-box is-${card.state}`}
      aria-label={ctx.tr('aiSuggest')}
      aria-busy={card.state === 'thinking' || card.state === 'long' || undefined}
      data-testid={testId}
    >
      <div className="ai-box-top">
        <Icon name="sparkle" size="sm" className="ai-box-mark" />
        <span className="label ai-box-name">{ctx.tr('aiClaude')}</span>
        {card.state === 'error' && <Icon name="alert" size="sm" className="ai-box-alert" />}
        {hasMenu && (
          <IconButton
            icon="more"
            label={ctx.tr('aiMore')}
            className="ai-more"
            onClick={() => setMenu(true)}
            data-testid="claude-more"
          />
        )}
      </div>

      {/* a new state fades in where the last one was (the skeleton has the suggestion's shape) */}
      <div key={card.state} className="ai-box-body" aria-live="polite">
        {(card.state === 'thinking' || card.state === 'long') && (
          <>
            <p className="ai-box-line" role="status" data-testid="claude-thinking">
              {card.line}
            </p>
            <span className="ai-skel" aria-hidden="true" />
            <span className="ai-skel is-short" aria-hidden="true" />
            {card.state === 'thinking' ? (
              <span className="ai-skel is-block" aria-hidden="true" />
            ) : (
              <button
                type="button"
                className="link-btn ai-box-hide"
                onClick={c.hide}
                data-testid="claude-hide"
              >
                {ctx.tr('aiHide')}
              </button>
            )}
          </>
        )}

        {card.state === 'error' && card.error && (
          <>
            <p className="ai-box-error" role="alert" data-testid="claude-error">
              {card.error.text}
            </p>
            {card.error.action === 'settings' && openSettings ? (
              <Button
                variant="secondary"
                size="sm"
                className="ai-box-act ai-small"
                onClick={openSettings}
                data-testid="claude-error-settings"
              >
                {ctx.tr('aiOpenSettings')}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                className="ai-box-act ai-small"
                onClick={c.ask}
                data-testid="claude-retry"
              >
                {ctx.tr('aiRetry')}
              </Button>
            )}
          </>
        )}

        {card.state === 'done' && (
          <div className="ai-box-done" data-testid="claude-suggestion">
            {card.head && (
              <p className="ai-box-head" data-testid="claude-head">
                {card.head}
              </p>
            )}
            <p className={card.head ? 'ai-box-why' : 'ai-box-text'} data-testid="claude-text">
              {card.text}
            </p>
            {card.rows.map((r) => (
              <div
                key={r.id}
                className={c.sending ? 'ai-change is-sending' : 'ai-change'}
                data-testid={`claude-change-${r.id}`}
              >
                <span className="ai-change-label">{r.label}</span>
                <span className="ai-change-value num">
                  <span className="ai-change-from">{r.from}</span>
                  <span aria-hidden="true"> → </span>
                  <span className="visually-hidden"> {ctx.tr('aiTo')} </span>
                  <span className="ai-change-to">{r.to}</span>
                </span>
              </div>
            ))}
            <span className="title-info ai-box-source">
              <span className="ai-box-source-text">{card.source}</span>
              <InfoTip
                text={ctx.tr('aiMistakesInfo')}
                label={ctx.tr('infoShow')}
                testId="claude-source-info"
              />
            </span>
            <div className="ai-box-actions">
              {card.apply && (
                <Button
                  size="sm"
                  className="ai-small"
                  icon="check"
                  busy={c.sending}
                  disabled={!card.apply.enabled || c.sending}
                  onClick={c.apply}
                  data-testid="claude-apply-go"
                >
                  {ctx.tr('aiApply')}
                </Button>
              )}
              {card.apply && !card.apply.enabled && card.apply.hint ? (
                <span className="hint ai-box-hint" data-testid="claude-apply-hint">
                  {card.apply.hint}
                </span>
              ) : (
                askMore
              )}
            </div>
          </div>
        )}

        {card.state === 'applied' && (
          <div className="ai-box-done" data-testid="claude-applied">
            <p className="ai-box-applied">
              <Icon name="check" size="sm" />
              {ctx.tr('aiApplied')}
            </p>
            {card.appliedText && <p className="ai-box-why">{card.appliedText}</p>}
            <div className="ai-box-actions">
              {card.undo && (
                <button
                  type="button"
                  className="link-btn"
                  disabled={c.undoing}
                  onClick={() => void c.undo()}
                  data-testid="claude-undo"
                >
                  {ctx.tr('aiUndo')}
                </button>
              )}
              {askMore}
            </div>
          </div>
        )}
      </div>

      {menu && (
        <Sheet
          title={ctx.tr('aiClaude')}
          titleId={menuTitle}
          onClose={() => setMenu(false)}
          closeLabel={ctx.tr('aiChatClose')}
          testId="claude-menu"
        >
          <NavList
            items={[
              {
                key: 'again',
                icon: 'sparkle',
                label: ctx.tr('aiAgain'),
                onClick: () => {
                  setMenu(false);
                  c.ask();
                },
                testId: 'claude-again',
              },
              ...(openSettings
                ? [
                    {
                      key: 'settings',
                      icon: 'sliders' as const,
                      label: ctx.tr('aiSettings'),
                      onClick: () => {
                        setMenu(false);
                        openSettings();
                      },
                      testId: 'claude-menu-settings',
                    },
                  ]
                : []),
            ]}
          />
        </Sheet>
      )}
      {chat && <ChatSheet shot={c.shot} onClose={() => setChat(false)} />}
    </section>
  );
}
